import React from "react";
import { websiteRequested } from "../../utils/urlFlags";
import { useShopOpen } from "../useShell";
import { DevMenu } from "./dev/DevMenu";
import { FpsReadout } from "./dev/FpsReadout";
import { HandsStrip } from "./HandsStrip";
import { HoldMusicLayer } from "./HoldMusicLayer";
import { ManualProvider } from "./manual/ManualProvider";
import { NavBar } from "./NavBar";
import { FloorSheet } from "./overlay/FloorSheet";
import { RewardFlightLayer } from "./payout/RewardFlightLayer";
import { StartMenu } from "./StartMenu";
import { StationSheet } from "./station/StationSheet";
import { BenchKeyHints } from "./bench/BenchKeyHints";
import { BenchStepBack } from "./bench/BenchStepBack";
import { BenchPlanBrowser, BenchPlanPile } from "./bench/BenchPlanPile";
import { BenchToolRail } from "./bench/BenchToolRail";
import { UnderBenchPanel } from "./bench/UnderBenchPanel";
import { useBenchDiveActive } from "./bench/useBenchDive";
import { StoreScreen } from "./store/StoreScreen";
import { StoreTripOverlay } from "./store-page/StoreTripOverlay";
import { LumberyardTripOverlay } from "./trips/LumberyardTripOverlay";
import { ScavengeTripOverlay } from "./trips/ScavengeTripOverlay";
import { SleepOverlay } from "./trips/SleepOverlay";
import { TripFade, useTripStaged } from "./trips/TripFade";
import { StoryLayer } from "./story/StoryLayer";
import { useScenePlaying } from "./story/useScenePlaying";
import { SuppliesSection } from "./SuppliesSection";
import { TutorialCards } from "./tutorial/TutorialCard";
import { TutorialSpotlightLayer } from "./tutorial/TutorialSpotlightLayer";

/**
 * The HUD chrome over the engine shell's canvas — the successor of
 * HomePage's overlay frame. The same layout contract holds: the canvas
 * runs edge to edge underneath, every strip here passes clicks through
 * (`pointer-events-none`) and only the chips re-enable them, and panels
 * never shove the canvas around.
 *
 * Before any shop is live this is the old Main's menu branch: the start
 * menu fills the sheet, and starting a game (its buttons boot the shop)
 * swaps it for the HUD.
 *
 * The wrappers are HomePage's: the NavBar along the top (clock,
 * balances, Skills, the manual's ?, Menu — with the journal, binder, and
 * pause menu behind them), the coach's column top-left, what's in hand
 * bottom-center, the supply tally folded under the top bar on the right.
 * The screen-anchored station and floor cards ride below the top bar's
 * z-40 so its buttons stay clickable over them; the world-pinned chips
 * and prompts live in OverlayRoot, not here.
 */
export const EngineHud: React.FC = () => {
  const open = useShopOpen();
  // Leaned over a bench, the corner chips fade: the bench view draws in
  // the canvas underneath them, and they'd be unreachable behind its
  // pointer surface anyway. The top bar stays — it deliberately rides
  // above the bench view — and so do the coach's cards, because the
  // guided opening's bench steps are read mid-dive: their column rises
  // over the bench view (z-36 against its z-35) without moving, so the
  // lean-in shifts nothing on the left edge.
  const benchDive = useBenchDiveActive();
  // A story scene takes the screen: the chrome fades so the floor, the
  // walk, and the box are all there is, the way a cutscene clears the
  // HUD. It comes back the moment the scene ends.
  const scene = useScenePlaying();
  const chipClass = `transition-opacity duration-150 ${
    benchDive || scene ? "opacity-0" : "opacity-100"
  }`;
  const chromeClass = `transition-opacity duration-300 ${
    scene ? "opacity-0" : "opacity-100"
  }`;
  if (!open) return <StartMenu />;

  return (
    // ManualProvider wraps the whole frame (old Main.tsx's nesting) so
    // `useManual` reaches every chip and card that points into the binder.
    <ManualProvider>
      {/* The page's one landmark, laid over the shop the canvas draws.
          `contents` so it groups the chrome without boxing it — every
          piece below is positioned against the window. */}
      <main className="contents">
        {/* The top bar, with the supply tally hanging under its right
            end in the same column — so the tally sits one rhythm step
            below the chip whatever height the day dial gives it.
            Deliberately outside the bench-dive fade: the cabinet is shop
            chrome, up and clickable above the bench view. */}
        <div
          inert={scene}
          className={`pointer-events-none absolute inset-x-0 top-0 z-40 flex flex-col items-end gap-3 px-6 pt-6 ${chromeClass}`}
        >
          <NavBar />
          <SuppliesSection />
        </div>

        {/* The coach's column: the tutorial cards. (The nudge home at
          close lives on the top bar's day dial, not down here.) */}
        <div
          inert={scene}
          className={`absolute left-6 top-6 w-80 space-y-3 ${
            benchDive ? "z-[36]" : "z-20"
          } ${chromeClass}`}
        >
          <TutorialCards />
        </div>

        {/* The ring the guided opening draws around chrome; in-world
          things wear TutorialHighlightView's outline instead. */}
        <TutorialSpotlightLayer />

        {/* pointer-events-none so the full-width strip doesn't eat clicks
          meant for what's underneath (the chip re-enables its buttons) */}
        <div
          inert={benchDive}
          className={`pointer-events-none absolute inset-x-0 bottom-6 z-20 flex justify-center px-6 ${chipClass}`}
        >
          <div className="pointer-events-auto">
            <HandsStrip />
          </div>
        </div>

        {/* The screen-anchored cards: whole-window surfaces, below the top
          bar's z-40 on purpose so its buttons stay clickable over them. */}
        <StationSheet />
        <FloorSheet />

        {/* The walkable store's chrome (cart corner + receipt card); its
          own null-gate keeps it off-screen at home. */}
        <StoreScreen />

        {/* Leaned over a bench: the tool rail is the mode selector down
          the left edge with the plan pile under it (one column, centred
          on the edge, clear of the coach's cards above), the plan
          drawer spreads over the view when the pile opens it, the
          under-bench pull sits bottom-centre, the key hints stack in
          the bottom-right corner, and the step-back tag — the one click
          that stands the player up — sits at the top centre. Both
          column members gate themselves, so the column is empty off the
          bench. A thought plays over the bench view (the first build's
          bench steps think out loud as each is done), so all of it goes
          inert under the story like the rest of the chrome. */}
        <div inert={scene} className="contents">
          <div className="pointer-events-none absolute inset-y-0 left-4 z-50 flex flex-col items-center justify-center gap-3">
            <BenchToolRail />
            <BenchPlanPile />
          </div>
          <BenchPlanBrowser />
          <BenchKeyHints />
          <BenchStepBack />
          <UnderBenchPanel />
        </div>

        {/* The away trips that cover the screen (each gates on its own
          `player.away` kind): the storefronts, the scavenging circuit,
          and the night between days. The wrapper
          re-enables pointer events under HudRoot's inert sheet (the
          same seam the manual's modal crosses), without a box of its
          own. A thought from the cab plays over the scavenging trip's
          screen, so it goes inert under the story like the rest. */}
        <div inert={scene} className="pointer-events-auto contents">
          <TripDestinations />
        </div>

        {/* The story: the cutscene's letterbox and the box the lines are
          read in, above the chrome it has faded (z-55/56). */}
        <StoryLayer />

        {/* The sale celebration, above everything (z-60): coins and the
          star fly to the top bar's readouts. */}
        <RewardFlightLayer />

        {/* The trip's black, above everything it covers. */}
        <TripFade />

        {/* Headless: elevator music while the wait key is held down. */}
        <HoldMusicLayer />

        {/* The playtesting cheats on the backtick key. Dev builds only —
          the guard is a compile-time constant, so production never
          mounts it. */}
        {process.env.NODE_ENV !== "production" && (
          <>
            <DevMenu />
            <FpsReadout />
          </>
        )}
      </main>
    </ManualProvider>
  );
};

/**
 * The destinations a trip takes over the screen with — held back while
 * the truck is still rolling out, which is a whole performance before
 * the destination should be up (TripTheater).
 */
const TripDestinations: React.FC = () => {
  if (!useTripStaged()) return null;
  return (
    <>
      {/* The Orange Box is a walkable place (StoreSceneRoot, swapped in
          by the SceneDirector); its storefront overlay takes the screen
          instead under ?website, as the future Orange Box website — see
          urlFlags.ts and issue #200. The same flag holds the venue at
          the shop, so the two never both show. */}
      {websiteRequested() && <StoreTripOverlay />}
      <LumberyardTripOverlay />
      <ScavengeTripOverlay />
      <SleepOverlay />
    </>
  );
};
